import { call, put, takeEvery, select } from "redux-saga/effects"

// Types
import NonConformityTypes from "ContainersForm/NonConformity/types"

// Actions
import { getAuditPlanByIdAction } from "ContainersForm/AuditPlans/actions"
import { postNotificationAction } from "Containers/Notifications/actions"
import { setSuccessCode } from "Containers/Messages/actions"
import {
  getFullAuditRecommendationsSuccessAction,
  getFullAuditRecommendationsAction,
  getFullAnomaliesSuccessAction,
  getFullIncidentsSuccessAction,
  getFullAnomaliesAction,
  getFullIncidentsAction,
} from "./actions"

// Selectors
import {
  selectCurrentToken,
  selectCurrentUser,
  selectTranslation,
} from "Containers/Login/selectors"

// Sagas
import { manageFilesETLsaga, postFileToS3, deleteMultiplesFiles } from "ContainersForm/ETL/saga"
import {
  manageSuccessGenerator,
  responseTypeIsSuccess,
  manageErrorGenerator,
  responseTypeIsError,
  manageWarningGenerator,
  responseTypeIsWarning,
} from "Containers/App/saga"

// Utils
import { getCors, postCors, patchCors, deleteCors } from "Utils/rulesCors"
import { getAllTasksValidatedNotif } from "./utils"
import { getRelatedFiles } from "ContainersForm/ETL/utils.js"
import { config } from "Utils/config"
import request from "Utils/request"
import { loadSagaTranslation } from "Utils/sagaTranslation"
import { selectAdminUsers } from "Containers/BusinessInformations/selectors"
import { generateNotification, generateTemplateNotification } from "Components/Notifications/utils"

// -------------------------------------//
// --------- NON CONFORMITIES ----------//
// -------------------------------------//
function* getFullAnomaliesSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const endPath = `nonconformities/anomaly`
  const requestURL = `${FORM_BACK_URL}/${endPath}`

  try {
    const response = yield call(request, requestURL, getCors(specificHeaders))
    if (responseTypeIsError(response)) {
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else {
      yield put(getFullAnomaliesSuccessAction(response.body))
    }
  } catch (err) {
    console.error("SAGA | getFullAnomaliesSaga - path /nonconformities => ", err)
  }
}

function* getFullIncidentsSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const endPath = `nonconformities/incident`
  const requestURL = `${FORM_BACK_URL}/${endPath}`

  try {
    const response = yield call(request, requestURL, getCors(specificHeaders))
    if (responseTypeIsError(response)) {
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else {
      yield put(getFullIncidentsSuccessAction(response.body))
    }
  } catch (err) {
    console.error("SAGA | getFullIncidentsSaga - path /nonconformities => ", err)
  }
}

function* getFullAuditRecommendationsSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const endPath = `nonconformities/auditRecommendation`
  const requestURL = `${FORM_BACK_URL}/${endPath}`

  try {
    const response = yield call(request, requestURL, getCors(specificHeaders))
    if (responseTypeIsError(response)) {
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else {
      yield put(getFullAuditRecommendationsSuccessAction(response.body))
    }
  } catch (err) {
    console.error("SAGA | getFullAuditRecommendationsSaga - path /nonconformities => ", err)
  }
}

function* createNonConformitySaga({ payload }) {
  const { nonConformity, nonConformityType, tasks, filesToManage } = payload
  const currentUser = yield select(selectCurrentUser)
  const adminUsers = yield select(selectAdminUsers)
  const token = yield select(selectCurrentToken)
  const trad = yield select(selectTranslation)
  const requestURL = `${config?.FORM_BACK_URL}/nonconformities/${nonConformityType}`

  try {
    // Fichiers de la non conformité
    if (filesToManage) {
      const previousNonConformityFiles = []
      const manageFilesResponse = yield call(manageFilesETLsaga, filesToManage)
      const { uploadedFiles, removedFiles } = manageFilesResponse
      nonConformity["relatedFiles"] = getRelatedFiles(
        previousNonConformityFiles,
        uploadedFiles,
        removedFiles
      )
    }

    // Fichiers dans chaque tâche
    for (const task of tasks) {
      const taskFilesToManage = []
      delete task.filesToManage
      if (taskFilesToManage) {
        const previousTaskFiles = task?.relatedFiles
        const manageFilesResponse = yield call(manageFilesETLsaga, taskFilesToManage)
        const { uploadedFiles, removedFiles } = manageFilesResponse
        task["relatedFiles"] = getRelatedFiles(previousTaskFiles, uploadedFiles, removedFiles)
      }
    }

    const response = yield call(request, requestURL, postCors({ nonConformity, tasks }, { token }))
    if (responseTypeIsError(response)) {
      console.error("SAGA | createNonConformity - path /nonconformities => ", response)
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else if (responseTypeIsWarning(response)) {
      yield call(manageWarningGenerator, { response, showMessageToUser: true })
    } else {
      // 1) Notif 1 :On informe le créateur et les admins de la création de la non-conformité
      const newNonConformityId = response?.body?.nonConformityId
      const usersIdsToInform = [...adminUsers?.map(user => user?.USER_ID), currentUser?.USER_ID]
      const paramsNotif1 = {
        currentUser: currentUser,
        usersIdsToInform: [...new Set(usersIdsToInform)],
        eventString:
          nonConformityType == "anomaly" ? trad?.A_CREER_ANOMALIE : trad?.A_CREER_INCIDENT,
        elementInBold: nonConformity?.name,
        redirectUrl: `/nonConformity-details/${nonConformityType}/${newNonConformityId}`,
        redirectState: { object: { ...nonConformity, _id: { $oid: newNonConformityId } } },
        channelId: "complication_created",
      }
      const notif1 = generateNotification(paramsNotif1)
      const infos_notif1 = generateTemplateNotification(
        `${nonConformityType}_complication_created`,
        {
          notif: notif1,
          currentUser,
          payload: {
            ...payload,
            newNonConformityId,
          },
          trad,
        }
      )
      yield put(postNotificationAction({ notif: notif1, infos_notif: infos_notif1 }))
      // 2) Notif 2 :On informe les assignés des tâches
      const usersIdsToInform2 = tasks?.map(task => task?.inChargeUserId)
      const paramsNotif2 = {
        currentUser: currentUser,
        usersIdsToInform: [...new Set(usersIdsToInform2)],
        eventString: trad?.ASSIGNED_YOU_TASKS_NON_CONFORMITY,
        elementInBold: nonConformity?.name,
        redirectUrl: `/nonConformity-details/${nonConformityType}/${newNonConformityId}`,
        redirectState: { object: { ...nonConformity, _id: { $oid: newNonConformityId } } },
        channelId: "complication_created",
      }
      const notif2 = generateNotification(paramsNotif2)
      const infos_notif2 = generateTemplateNotification("complication_assigned_tasks", {
        notif: notif2,
        currentUser,
        payload: {
          ...payload,
          newNonConformityId,
        },
        trad,
      })
      yield put(postNotificationAction({ notif: notif2, infos_notif: infos_notif2 }))
      //
      yield call(manageSuccessGenerator, { response, showMessageToUser: true })
      if (nonConformityType === "anomaly") {
        yield put(getFullAnomaliesAction())
      } else if (nonConformityType === "incident") {
        yield put(getFullIncidentsAction())
      } else if (nonConformityType === "auditRecommendation") {
        yield put(getFullAuditRecommendationsAction())
      }
    }
  } catch (err) {
    console.error("SAGA | createNonConformity - path /nonconformities => ", err)
  }
}

function* patchNonConformitySaga({ payload }) {
  const { nonConformity, nonConformityType, tasks, filesToManage } = payload
  const nonConformityId = nonConformity?._id?.$oid

  const token = yield select(selectCurrentToken)
  const specificHeaders = { token }
  const { FORM_BACK_URL } = config

  const endPath = `/nonconformity/${nonConformityType}/${nonConformityId}`
  const requestURL = `${FORM_BACK_URL}${endPath}`

  delete nonConformity._etag
  delete nonConformity.controls
  delete nonConformity.creator

  try {
    // Fichiers de la non conformité
    if (filesToManage) {
      const previousNonConformityFiles = nonConformity?.relatedFiles
      const manageFilesResponse = yield call(manageFilesETLsaga, filesToManage)
      const { uploadedFiles, removedFiles } = manageFilesResponse
      nonConformity["relatedFiles"] = getRelatedFiles(
        previousNonConformityFiles,
        uploadedFiles,
        removedFiles
      )
    }

    // Fichiers dans chaque tâche
    for (const task of tasks) {
      const taskFilesToManage = task?.filesToManage
      delete task.filesToManage
      if (taskFilesToManage) {
        const previousTaskFiles = task?.relatedFiles
        const manageFilesResponse = yield call(manageFilesETLsaga, taskFilesToManage)
        const { uploadedFiles, removedFiles } = manageFilesResponse
        task["relatedFiles"] = getRelatedFiles(previousTaskFiles, uploadedFiles, removedFiles)
      }
    }

    const response = yield call(
      request,
      requestURL,
      patchCors({ nonConformity, tasks }, specificHeaders)
    )

    if (responseTypeIsError(response)) {
      console.error(
        "SAGA | patchNonConformityAction /nonconformity/:type/:id - error => ",
        response
      )
    } else if (responseTypeIsWarning(response)) {
      yield call(manageWarningGenerator, { response, showMessageToUser: true })
    } else {
      yield call(manageSuccessGenerator, { response, showMessageToUser: true })
      if (nonConformityType === "anomaly") {
        yield put(getFullAnomaliesAction())
      } else if (nonConformityType === "incident") {
        yield put(getFullIncidentsAction())
      } else if (nonConformityType === "auditRecommendation")
        yield put(getFullAuditRecommendationsAction())
    }
  } catch (err) {
    console.error("SAGA | patchNonConformityAction /nonconformity/:type/:id - error => ", err)
  }
}

function* deleteNonConformitySaga({ payload }) {
  const { nonConformityType, nonConformityId, relatedFiles, auditPlanId } = payload
  const token = yield select(selectCurrentToken)

  try {
    if (Array.isArray(relatedFiles)) {
      yield call(deleteMultiplesFiles, relatedFiles)
    }

    const response = yield call(
      request,
      `${config?.FORM_BACK_URL}/nonconformity/${nonConformityType}/${nonConformityId}`,
      deleteCors({ token })
    )

    if (responseTypeIsError(response)) {
      console.error(
        "SAGA | deleteNonConformityAction /nonconformity/:type/:id - error => ",
        response
      )
    } else if (nonConformityType === "anomaly") {
      yield put(getFullAnomaliesAction())
    } else if (nonConformityType === "incident") {
      yield put(getFullIncidentsAction())
    } else if (nonConformityType === "auditRecommendation") {
      yield put(getFullAuditRecommendationsAction())
      if (auditPlanId) yield put(getAuditPlanByIdAction({ auditPlanId }))
    } else if (nonConformityType === "recommandation") {
      yield put(setSuccessCode("SUCCESS_TO_DELETE_RECOMMENDATION"))
    }
  } catch (err) {
    console.error("SAGA | deleteNonConformityAction /nonconformity/:type/:id - error => ", err)
  }
}

function* patchStatutNonConformitySaga({ payload }) {
  const { nonConformity } = payload
  const { type, translatedInitialStatus, translatedNewStatus, name, creatorId } = nonConformity
  const currentUser = yield select(selectCurrentUser)
  const adminUsers = yield select(selectAdminUsers)
  const nonConformityId = nonConformity?._id?.$oid
  const token = yield select(selectCurrentToken)
  const trad = yield select(selectTranslation)
  const usersIdsToInform = [...adminUsers?.map(user => user?.USER_ID), creatorId]
  const paramsNotif = {
    currentUser: currentUser,
    usersIdsToInform: [...new Set(usersIdsToInform)],
    eventString: `${trad?.UPDATED_STATUS_NON_CONFORMITY_TO} ${trad?.FROM} ${translatedInitialStatus} ${trad?.TO} ${translatedNewStatus} concernant`,
    elementInBold: name,
    redirectUrl: `/nonConformity-details/${type}/${nonConformityId}`,
    redirectState: { object: nonConformity },
    channelId: "complication_created",
  }
  const notif = generateNotification(paramsNotif)
  const infos_notif = generateTemplateNotification("complication_patch_statut", {
    notif,
    currentUser,
    payload: {
      ...payload,
      nonConformityId,
    },
    trad,
  })

  const requestURL = `${config.FORM_BACK_URL}/statut-nonconformity/${type}/${nonConformityId}`

  try {
    const response = yield call(request, requestURL, patchCors({ nonConformity }, { token }))
    if (responseTypeIsError(response)) {
      console.error(
        "SAGA | patchStatutNonConformitySaga /statut-nonconformity/:type/:id - error => ",
        response
      )
    } else if (responseTypeIsWarning(response)) {
      yield call(manageWarningGenerator, { response, showMessageToUser: true })
    } else {
      yield call(manageSuccessGenerator, { response, showMessageToUser: true })
      yield put(postNotificationAction({ notif, infos_notif }))
      if (type === "anomaly") {
        yield put(getFullAnomaliesAction())
      } else if (type === "incident") {
        yield put(getFullIncidentsAction())
      } else if (type === "auditRecommendation") {
        yield put(getFullAuditRecommendationsAction())
      }
    }
  } catch (err) {
    console.error(
      "SAGA | patchStatutNonConformitySaga /statut-nonconformity/:type/:id - error => ",
      err
    )
  }
}

// -------------------------------------//
// --------------- TASKS ---------------//
// -------------------------------------//
function* validatedTaskSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const currentUser = yield select(selectCurrentUser)
  const trad = yield call(loadSagaTranslation, currentUser?.LANGUAGE_ID)
  const specificHeaders = { token: token }
  const { taskId, validationComment, previousFilesAttacheds, filesTasks, nonConformityType } =
    payload
  const requestURL = `${config?.FORM_BACK_URL}/validateTask/${taskId}`
  let documentsTasks = [...previousFilesAttacheds]

  console.log("filesTasks =>", filesTasks)

  try {
    // 1) we loop over the files to add them to the ETL
    for (let file of filesTasks) {
      const responsePostFile = yield call(postFileToS3, file)
      if (responseTypeIsError(responsePostFile)) {
        yield call(manageErrorGenerator, {
          response: responsePostFile,
          showMessageToUser: true,
        })
      } else {
        const newFilePosted = {
          name: responsePostFile?.body?.name,
          uuid: responsePostFile?.body?.uuid,
          s3: true,
        }
        documentsTasks.push(newFilePosted)
      }
    }

    const tasksPatch = {
      taskId: taskId,
      validationComment: validationComment,
      filesAttachedIds: documentsTasks,
      nonConformityType,
    }

    const response = yield call(request, requestURL, patchCors(tasksPatch, specificHeaders))
    if (responseTypeIsError(response)) {
      console.error("SAGA | validatedTask => ", response)
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else {
      const { allTasksAreValidated, nonConformityAdmin, nonConformityName } = response?.body
      const paramsNotif = {
        nonConformityType,
        nonConformityName,
        nonConformityAdmin,
        currentUser,
        trad,
      }
      const notification = getAllTasksValidatedNotif(paramsNotif)
      const infos_notif = generateTemplateNotification("complication_validated_task", {
        notif: notification,
        currentUser,
        payload,
        trad,
      })
      if (nonConformityType === "anomaly") yield put(getFullAnomaliesAction())
      if (nonConformityType === "incident") yield put(getFullIncidentsAction())
      if (nonConformityType === "auditRecommendation")
        yield put(getFullAuditRecommendationsAction())
      if (allTasksAreValidated)
        yield put(postNotificationAction({ notif: notification, infos_notif }))
    }
  } catch (err) {
    console.error("SAGA | validatedTask => ", err)
  }
}

function* nonConformitySaga() {
  // NON CONFORMITIES
  yield takeEvery(NonConformityTypes.GET_FULL_ANOMALIES, getFullAnomaliesSaga)
  yield takeEvery(NonConformityTypes.GET_FULL_INCIDENTS, getFullIncidentsSaga)
  yield takeEvery(
    NonConformityTypes.GET_FULL_AUDIT_RECOMMENDATIONS,
    getFullAuditRecommendationsSaga
  )
  yield takeEvery(NonConformityTypes.CREATE_NON_CONFORMITY, createNonConformitySaga)
  yield takeEvery(NonConformityTypes.PATCH_NON_CONFORMITY, patchNonConformitySaga)
  yield takeEvery(NonConformityTypes.PATCH_STATUT_NON_CONFORMITY, patchStatutNonConformitySaga)
  yield takeEvery(NonConformityTypes.DELETE_NON_CONFORMITY, deleteNonConformitySaga)
  // TASKS
  yield takeEvery(NonConformityTypes.VALIDATED_TASK, validatedTaskSaga)
}

export default nonConformitySaga
